import { handled, type Route, type Stop } from '@day';
import { useStore } from '../app/store';
import { backToDay, openCourier, openPerson, openRoute, openStop, ui } from '../app/ui';
import { dayTime, length, money, plural, streetOf, time, two } from '../app/words';
import { keyOf, routeTitle, type Model } from '../data/model';
import { Av, Dot, More, Segs, StatusIcon, StatusNow } from '../ui/bits';
import { Icon } from '../ui/Icon';
import * as act from '../app/actions';
import { LookRow, openWords, routeLine, routeOfPerson, Summary } from './Live';
import { copyText, trackingLink } from './StopBody';

/** Live's List: the map hidden, the same day as tables (control-room-web.html `dayTable`, `routeTable`). */

export function handCell(m: Model, balance: number, first: string) {
  return balance > 0 ? money(balance, m.currency) : balance < 0 ? `you owe ${first} ${money(-balance, m.currency)}` : '–';
}

export function DayTable({ m }: { m: Model }) {
  // With the routes each courier finished today, a route that started before midnight and ended after it included.
  const routes = [...m.routes, ...m.people.flatMap((p) => p.day?.finished ?? []).filter((r) => !m.routes.includes(r))].sort((a, b) => order(a) - order(b));
  // Not out yet: no route running, waiting or finished today (as Live and Couriers have it).
  const without = m.people.filter((p) => !m.routes.some((r) => r.riderId === p.uid) && !p.day?.finished.length);
  return (
    <>
      <div className="card" style={{ padding: '18px 20px', marginBottom: 12 }}>
        <Summary m={m} />
      </div>
      {m.looks.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="lsec">
            <span className="eyebrow">Needs a look</span>
            <span className="eyebrow">{m.looks.length}</span>
          </div>
          <div className="rows">
            {m.looks.map((x) => (
              <LookRow key={x.id} x={x} />
            ))}
          </div>
          <div style={{ height: 6 }} />
        </div>
      )}
      <div className="card">
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Courier</th>
                <th>Route</th>
                <th>Today</th>
                <th style={{ width: 220 }}>Progress</th>
                <th className="num">Done about</th>
                <th className="num low">Distance</th>
                <th className="num">To hand in</th>
                <th className="act" />
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => {
                const p = m.person(r.riderId);
                const finish = r.state === 'running' ? p?.day?.finishAt : r.state === 'done' || r.state === 'ended' ? (r.completedAt ?? r.endedAt) : null;
                return (
                  <tr key={r.id} className="r" data-ctx={`route:${r.id}`} onClick={() => openRoute(r.id)}>
                    <td>
                      <span className="row">
                        <Av p={p} />
                        <span className="addr">{p?.name ?? 'A courier'}</span>
                      </span>
                    </td>
                    <td>{routeTitle(r, m.zone)}</td>
                    <td>
                      {r.state === 'running' ? (
                        <span className="chip">
                          <Dot p={p} />
                          On a route
                        </span>
                      ) : r.state === 'waiting' ? (
                        <span className="chip">
                          <Icon name="circleDashed" size="sm" />
                          Waiting to start
                        </span>
                      ) : r.state === 'declined' ? (
                        <span className="chip neg">
                          <Icon name="warningCircle" size="sm" />
                          Can’t take it
                        </span>
                      ) : (
                        <span className="chip">
                          <Icon name="flagCheckered" size="sm" />
                          {r.state === 'ended' ? 'Ended early' : 'Finished'}
                        </span>
                      )}
                    </td>
                    <td>
                      {r.state === 'waiting' || r.state === 'declined' ? (
                        <span className="ink2">{plural(r.stops.length, 'stop')}</span>
                      ) : (
                        <div className="row" style={{ gap: 10 }}>
                          <div className="grow">
                            <Segs stops={r.stops} />
                          </div>
                          <span className="mono ink2" style={{ fontSize: 13 }}>
                            {r.stops.filter(handled).length}/{r.stops.length}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="num mono">{finish ? time(finish, m.zone) : '–'}</td>
                    <td className="num mono low">{r.km ? length(r.km * 1000) : '–'}</td>
                    <td className="num mono">{r.state === 'waiting' || r.state === 'declined' || !p ? '–' : handCell(m, p.balance, p.first)}</td>
                    <td className="act" onClick={(e) => e.stopPropagation()}>
                      <More menu={`route:${r.id}`} />
                    </td>
                  </tr>
                );
              })}
              {without.map((p) => (
                <tr key={p.uid} className="r" data-ctx={`courier:${p.uid}`} onClick={() => openPerson(p.uid, routeOfPerson(m))}>
                  <td>
                    <span className="row">
                      <Av p={p} faded />
                      <span className="addr">{p.name}</span>
                    </span>
                  </td>
                  <td className="ink2">–</td>
                  <td>
                    <span className="chip">{p.day?.leftOpen ? openWords(m, p.day.leftOpen) : p.paused ? 'Paused' : 'Not out yet'}</span>
                  </td>
                  <td className="ink2">{p.day?.presence?.at ? `Last seen ${dayTime(p.day.presence.at, m.now, m.zone)}, rough area` : ''}</td>
                  <td className="num">–</td>
                  <td className="num low">–</td>
                  <td className="num mono">{handCell(m, p.balance, p.first)}</td>
                  <td className="act" onClick={(e) => e.stopPropagation()}>
                    <More menu={`courier:${p.uid}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/** Running first, then waiting, then done: the order the list shows them in. */
const order = (r: Route) => ({ running: 0, declined: 1, waiting: 2, done: 3, ended: 3 })[r.state];

/** A route's own table, with its stops selected for bulk actions. */
export function RouteTable({ m, r }: { m: Model; r: Route }) {
  const s = useStore(ui);
  const p = m.person(r.riderId);
  const keys = r.stops.map(keyOf);
  const sel = keys.filter((k) => s.sel.has(k));
  const all = keys.length > 0 && sel.length === keys.length;
  const toggle = (k: string) =>
    ui.set((x) => {
      const next = new Set(x.sel);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return { sel: next };
    });
  return (
    <>
      <div className="card" style={{ padding: '16px 20px', marginBottom: 12 }}>
        <div className="row">
          <button className="iconbtn" data-tip="Back to today · Esc" aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <Av p={p} size="lg" />
          <div className="grow">
            <button className="title" style={{ textAlign: 'left' }} onClick={() => p && openCourier(p.uid)}>
              {p?.first ?? 'A courier'} · {routeTitle(r, m.zone)}
            </button>
            <div className="cap">{routeLine(m, r)}</div>
          </div>
          <button className="iconbtn" data-menu={`route:${r.id}`} data-tip="Route actions" aria-label="Route actions">
            <Icon name="dotsThreeVertical" size="lg" />
          </button>
        </div>
      </div>
      <div className="card">
        <div className="tools">
          {sel.length > 0 && <BulkBar m={m} stops={sel.map((k) => m.stop(k)).filter((x): x is Stop => !!x)} />}
          <span className="grow" />
          <span className="cap">{plural(r.stops.length, 'stop')}</span>
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th className="cb">
                  <button
                    className={`cbx${all ? ' on' : ''}`}
                    role="checkbox"
                    aria-checked={all}
                    aria-label={all ? 'Clear these' : 'Select these'}
                    data-tip={all ? 'Clear these' : 'Select these'}
                    onClick={() => ui.set((x) => ({ sel: all ? new Set([...x.sel].filter((k) => !keys.includes(k))) : new Set([...x.sel, ...keys]) }))}
                  >
                    {all && <Icon name="check" />}
                  </button>
                </th>
                <th className="st">No.</th>
                <th className="si" />
                <th>Address</th>
                <th>Status</th>
                <th className="low">Customer</th>
                <th className="act" />
              </tr>
            </thead>
            <tbody>
              {r.stops.map((x) => {
                const k = keyOf(x);
                const on = s.sel.has(k);
                return (
                  <tr key={k} className={`r${s.rec?.id === k ? ' sel' : ''}`} data-stop={k} data-ctx={`stop:${k}`} data-drag={r.state === 'waiting' ? `${r.id}|${x.id}` : undefined} onClick={() => openStop(k)}>
                    <td className="cb" onClick={(e) => e.stopPropagation()}>
                      <button className={`cbx${on ? ' on' : ''}`} role="checkbox" aria-checked={on} aria-label={`Select ${streetOf(x)}`} onClick={() => toggle(k)}>
                        {on && <Icon name="check" />}
                      </button>
                    </td>
                    <td className="st">
                      <span className="mono" style={{ color: `color-mix(in srgb,${p?.hex ?? '#5E594F'} 82%,var(--ink))` }}>
                        {two(x.n)}
                      </span>
                    </td>
                    <td className="si">
                      <StatusIcon s={x} today={m.today} />
                    </td>
                    <td>
                      <div className="addr">{streetOf(x)}</div>
                      <div className="area">{x.area}</div>
                    </td>
                    <td>
                      <StatusNow s={x} m={m} sameRoute routeName={routeTitle(r, m.zone)} />
                    </td>
                    <td className="nw low">{x.name ?? <span className="ink2">–</span>}</td>
                    <td className="act" onClick={(e) => e.stopPropagation()}>
                      <More menu={`stop:${k}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/** What can be done with the stops selected (control-room-web.html `bulkBar`): moving and taking off only on routes not started. */
export function BulkBar({ m, stops }: { m: Model; stops: Stop[] }) {
  const links = stops.filter((s) => s.trackingCode && !s.handover);
  // Only routes waiting to start change from here: a started one is the courier's, a declined one goes to someone else.
  const movable = stops.filter((s) => m.route(s.routeId)?.state === 'waiting');
  const started = stops.length - movable.length;
  return (
    <>
      <b>{plural(stops.length, 'stop')} selected</b>
      <span data-tip={!movable.length ? 'Their routes have started or were declined: they don’t change from here' : started ? `${plural(started, 'stop')} on routes started or declined stay where they are` : undefined}>
        <button className="btn sm" disabled={!movable.length} data-menu={`bulkmove:${movable.map(keyOf).join(',')}`}>
          <Icon name="arrowsClockwise" size="sm" />
          Move to…
        </button>
      </span>
      <span data-tip={!movable.length ? 'Their routes have started or were declined: they change on the couriers’ phones' : 'Delivery or pickup, time at the stop, deliver by'}>
        <button className="btn sm" disabled={!movable.length} data-menu={`change:${movable.map(keyOf).join(',')}`}>
          <Icon name="notePencil" size="sm" />
          Change
          <Icon name="caretDown" size="sm" />
        </button>
      </span>
      {links.length > 0 ? (
        <button className="btn sm" onClick={() => copyText(links.map((s) => `${streetOf(s)}: ${trackingLink(s.trackingCode!)}`).join('\n'), `${plural(links.length, 'tracking link')} copied`)}>
          <Icon name="copy" size="sm" />
          Copy {plural(links.length, 'tracking link')}
        </button>
      ) : (
        <span data-tip={stops.some((s) => s.state === 'waiting') ? 'The links are made when the routes start' : 'No tracking links on these'}>
          <button className="btn sm" disabled>
            <Icon name="copy" size="sm" />
            Copy tracking links
          </button>
        </span>
      )}
      <button className="btn sm danger" disabled={!movable.length} onClick={() => act.takeOffStops(movable.map(keyOf))}>
        <Icon name="trash" size="sm" />
        Take off
      </button>
      <button className="btn sm" onClick={() => ui.set({ sel: new Set() })}>
        Clear the selection
      </button>
    </>
  );
}
